import { useState, type FormEvent, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginBrandBg } from '@/assets/images';
import { BrandMarkIcon, SparkleOnDarkIcon } from '@/components/icons';
import { Button } from '@/components/ui';
import { buildUserForLogin } from '@/config/buildLoginUser';
import { APP_BRAND } from '@/config/brand';
import { getBrandConfig } from '@/config/brandConfig';
import {
  isAllowedEmail,
  isValidPassword,
  normalizeEmail,
  workspaceRouteForRole,
} from '@/config/authAllowlist';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/utils/helpers';

const brandConfig = getBrandConfig();
const isHidBrand = APP_BRAND === 'hid';

const agBrandPanelStyle: CSSProperties = { backgroundImage: `url(${loginBrandBg})` };

function EyeIcon({ hidden }: { hidden: boolean }) {
  if (hidden) {
    return (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
        <path
          d="M2.5 10s2.5-5 7.5-5 7.5 5 7.5 5-2.5 5-7.5 5-7.5-5-7.5-5Z"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
        <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.4" />
        <path d="M3 3l14 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
      <path
        d="M2.5 10s2.5-5 7.5-5 7.5 5 7.5 5-2.5 5-7.5 5-7.5-5-7.5-5Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    const normalized = normalizeEmail(email);
    if (!isAllowedEmail(normalized)) {
      setError('This email is not authorized. Please use your company account.');
      return;
    }
    if (!isValidPassword(password)) {
      setError('Incorrect password. Try again.');
      return;
    }

    setLoading(true);
    try {
      const user = buildUserForLogin(normalized);
      login(user);
      navigate(workspaceRouteForRole(user.role), { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <aside
        className="login-page__brand"
        style={isHidBrand ? undefined : agBrandPanelStyle}
        aria-hidden={false}
      >
        {isHidBrand ? (
          <>
            <div className="login-page__brand-hid">
              <img
                src={brandConfig.hidLogo}
                alt="HID Agent Guard"
                className="login-page__hid-logo"
                width={120}
                height={50}
              />
            </div>
            <h2 className="login-page__tagline login-page__tagline--hid">
              <span className="login-page__tagline-line login-page__tagline-line--lead">
                The smarter way to
                <img
                  src={brandConfig.hidStarIcon}
                  alt=""
                  className="login-page__tagline-star"
                  width={25}
                  height={25}
                  aria-hidden
                />
              </span>
              <span className="login-page__tagline-line">manage access control.</span>
            </h2>
          </>
        ) : (
          <>
            <div className="login-page__brand-top">
              <BrandMarkIcon size={32} className="login-page__brand-mark" />
              <span className="login-page__brand-name">Agent Guard</span>
            </div>
            <h2 className="login-page__tagline">
              <span className="login-page__tagline-line">
                The smarter way to
                <SparkleOnDarkIcon size={32} className="login-page__tagline-sparkle" />
              </span>
              <span className="login-page__tagline-line">manage access control.</span>
            </h2>
          </>
        )}
      </aside>

      <main className="login-page__main">
        <div className="login-page__form-wrap">
          <h1 className="login-page__title">Log in to Agent Guard</h1>

          <form className="login-page__form" onSubmit={handleSubmit} noValidate>
            <div className="login-page__field">
              <label className="login-page__label" htmlFor="login-email">
                Email
              </label>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                required
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="login-page__input"
              />
            </div>

            <div className="login-page__field">
              <label className="login-page__label" htmlFor="login-password">
                Password
              </label>
              <div className="login-page__password-wrap">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={cn('login-page__input', 'login-page__input--password')}
                />
                <button
                  type="button"
                  className="login-page__password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <EyeIcon hidden={!showPassword} />
                </button>
              </div>
            </div>

            <p className="login-page__forgot-row">
              <span className="login-page__forgot">Forgot password?</span>
            </p>

            {error ? (
              <p className="login-page__error" role="alert">
                {error}
              </p>
            ) : null}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="login-page__submit"
              disabled={loading}
            >
              {loading ? 'Logging in…' : 'Log in'}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}
